import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { FC } from 'react'

interface SetAsideReasonModalProps {
  isOpen: boolean
  wordLabel: string
  onConfirm: (reason?: string) => void
  onCancel: () => void
}

const QUICK_REASONS = [
  'Too complex',
  'Not the right vibe',
  'Saving for later',
  'Other'
]

/**
 * SetAsideReasonModal - Modal for capturing why a word is being set aside
 *
 * Provides quick-reason chips for common scenarios and optional custom textarea
 * for detailed explanation. Saves reason with word timeline for future reference.
 */
export const SetAsideReasonModal: FC<SetAsideReasonModalProps> = ({
  isOpen,
  wordLabel,
  onConfirm,
  onCancel
}) => {
  const [selectedQuickReason, setSelectedQuickReason] = useState<string | null>(null)
  const [customReason, setCustomReason] = useState('')
  const modalRef = useRef<HTMLDivElement>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedQuickReason(null)
      setCustomReason('')
      // Focus modal for keyboard navigation
      setTimeout(() => {
        modalRef.current?.focus()
      }, 0)
    }
  }, [isOpen])

  // Handle ESC key to cancel
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onCancel])

  const handleConfirm = () => {
    // Determine final reason: custom text takes precedence, then quick reason, then undefined
    let finalReason: string | undefined

    const trimmedCustom = customReason.trim()
    if (trimmedCustom) {
      finalReason = trimmedCustom
    } else if (selectedQuickReason) {
      finalReason = selectedQuickReason
    }

    onConfirm(finalReason)
  }

  const handleQuickReasonClick = (reason: string) => {
    // Toggle selection
    setSelectedQuickReason(prev => prev === reason ? null : reason)
  }

  if (!isOpen) return null

  // Use portal to render at document body level, escaping any container constraints
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={onCancel}
        data-testid="set-aside-reason-modal-backdrop"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] bg-white rounded-lg shadow-xl p-6 w-96 max-w-[90vw]"
        role="dialog"
        aria-labelledby="set-aside-modal-title"
        tabIndex={-1}
        data-testid="set-aside-reason-modal"
      >
        <h2
          id="set-aside-modal-title"
          className="text-lg font-semibold text-gray-900 mb-2"
        >
          Set Aside: {wordLabel}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Why are you setting this word aside? (optional)
        </p>

        {/* Quick reason chips */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Quick reasons:
          </p>
          <div className="flex flex-wrap gap-2">
            {QUICK_REASONS.map(reason => (
              <button
                key={reason}
                type="button"
                onClick={() => handleQuickReasonClick(reason)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  selectedQuickReason === reason
                    ? 'bg-gray-700 text-white border-gray-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`quick-reason-${reason.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {reason}
              </button>
            ))}
          </div>
        </div>

        {/* Custom reason textarea */}
        <div className="mb-4">
          <label
            htmlFor="custom-reason"
            className="block text-xs font-semibold text-gray-700 mb-2"
          >
            Or add a custom note:
          </label>
          <textarea
            id="custom-reason"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            className="w-full h-24 p-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-gray-500"
            placeholder="Add more details if needed..."
            data-testid="custom-reason-textarea"
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            data-testid="set-aside-reason-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors"
            data-testid="set-aside-reason-save"
          >
            Set Aside
          </button>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          <kbd className="px-1 py-0.5 bg-gray-100 rounded">ESC</kbd> to cancel
        </div>
      </div>
    </>,
    document.body
  )
}
